<!-- The chat window's header bar: the title drags the pane, the pane's edges resize it, and the
     size and place are remembered. -->
<div class="review-header"
    appDraggableOverlayPane [dragLayoutKey]="layoutKey"
    [appResizableOverlayPane]="layoutKey" [resizeMinWidth]="520" [resizeMinHeight]="360">
    <mat-icon class="kind">fact_check</mat-icon>
    <div class="title">
        <span class="name">{{ fill?.fileName || 'Completed form' }}</span>
        <span class="subtitle" *ngIf="fill?.counts as c; else plainSubtitle">
            {{ c.filled }} of {{ c.total }} answered{{ c.needsCheck ? ' · ' + c.needsCheck + ' to check' : '' }}{{ fill?.status === 'accepted' ? ' · accepted' : '' }}
        </span>
        <ng-template #plainSubtitle><span class="subtitle">Review the answers before accepting</span></ng-template>
    </div>
    <button mat-icon-button (click)="close()" matTooltip="Close" aria-label="Close"><mat-icon>close</mat-icon></button>
</div>

<div class="review-content">

    <div class="review-loading" *ngIf="loading">
        <mat-spinner diameter="28"></mat-spinner><span>Loading the completed form…</span>
    </div>

    <div class="review-error" *ngIf="error">
        <mat-icon>error_outline</mat-icon><span>{{ error }}</span>
    </div>

    <ng-container *ngIf="fill && !loading">

        <div class="review-toolbar">
            <span class="count-chip filled" *ngIf="fill.counts">{{ fill.counts.filled }} filled</span>
            <span class="count-chip blank" *ngIf="fill.counts?.blank">{{ fill.counts?.blank }} blank</span>
            <span class="count-chip warn" *ngIf="fill.counts?.needsCheck">{{ fill.counts?.needsCheck }} to check</span>
            <span class="count-total" *ngIf="fill.counts">of {{ fill.counts.total }}</span>
            <span class="count-chip edited" *ngIf="editCount">{{ editCount }} changed by you</span>
            <span class="count-chip warn" *ngIf="overflowCount">{{ overflowCount }} too long</span>

            <span class="spacer"></span>

            <label class="search-field">
                <mat-icon>search</mat-icon>
                <input type="search" [(ngModel)]="search" placeholder="Find a question" aria-label="Find a question" />
            </label>

            <mat-slide-toggle [(ngModel)]="onlyNeedsYou" class="needs-toggle">
                Only what needs me
            </mat-slide-toggle>
        </div>

        <div class="review-body">

            <!-- ══ LEFT: the form itself ══════════════════════════════════════════════ -->
            <div class="review-form">

                <p class="muted empty" *ngIf="!visibleFields.length">
                    Nothing matches that filter.
                </p>

                <ng-container *ngFor="let f of visibleFields; let i = index">

                    <button type="button" class="page-divider" *ngIf="isFirstOnPage(f, i)"
                        (click)="goToPage(f.page)"
                        matTooltip="Turn the document to this page">Page {{ f.page + 1 }}</button>

                    <div class="section-heading" *ngIf="isFirstInSection(f, i)">{{ question(f).section }}</div>

                    <div class="form-field" [class.selected]="selectedFieldId === f.fieldId"
                        [class.edited]="edits.has(f.fieldId)"
                        [class.blank]="f.disposition === 'blank' && !edits.has(f.fieldId)"
                        (click)="selectField(f.fieldId)">

                        <label class="field-question">
                            <span class="item-ref" *ngIf="f.itemRef">{{ f.itemRef }}</span>
                            {{ question(f).question }}
                            <span class="required-star" *ngIf="f.required"
                                matTooltip="The form requires this">*</span>
                        </label>

                        <!-- The control is typed to the field. Each one makes a whole class of
                             invalid value impossible to enter in the first place. -->
                        <div class="field-control" (click)="$event.stopPropagation()">

                            <div class="signature-note" *ngIf="f.kind === 'signature'">
                                <mat-icon>draw</mat-icon>
                                <span>You sign this yourself after downloading.</span>
                            </div>

                            <mat-checkbox *ngIf="f.kind === 'checkbox'" [checked]="isChecked(f)"
                                (change)="setValue(f, $event.checked)"
                                [disabled]="fill.status === 'accepted'">
                                {{ isChecked(f) ? 'Yes' : 'No' }}
                            </mat-checkbox>

                            <mat-radio-group *ngIf="f.kind === 'radio' && f.options?.length"
                                [value]="valueOf(f)" (change)="setValue(f, $event.value)"
                                class="radio-row">
                                <mat-radio-button *ngFor="let o of f.options" [value]="o"
                                    [disabled]="fill.status === 'accepted'">{{ o }}</mat-radio-button>
                            </mat-radio-group>

                            <!-- A choice: a field that opens a menu of the options, as the chat's ⋮ menu does. -->
                            <ng-container *ngIf="f.kind === 'choice' && f.options?.length">
                                <button type="button" class="cura-select full" [matMenuTriggerFor]="choiceMenu"
                                    [disabled]="fill.status === 'accepted'">
                                    <span class="cura-select-text" [class.placeholder]="!valueOf(f)">{{ valueOf(f) || 'Leave blank' }}</span>
                                    <mat-icon class="cura-select-arrow">arrow_drop_down</mat-icon>
                                </button>
                                <mat-menu #choiceMenu="matMenu" class="cura-menu review">
                                    <button mat-menu-item [class.selected]="!valueOf(f)" (click)="setValue(f, '')">
                                        <mat-icon>{{ !valueOf(f) ? 'radio_button_checked' : 'radio_button_unchecked' }}</mat-icon>
                                        <span>Leave blank</span>
                                    </button>
                                    <button mat-menu-item *ngFor="let o of f.options" [class.selected]="valueOf(f) === o"
                                        (click)="setValue(f, o)">
                                        <mat-icon>{{ valueOf(f) === o ? 'radio_button_checked' : 'radio_button_unchecked' }}</mat-icon>
                                        <span>{{ o }}</span>
                                    </button>
                                </mat-menu>
                            </ng-container>

                            <mat-form-field *ngIf="f.kind === 'comb'" appearance="outline"
                                class="full comb" subscriptSizing="dynamic">
                                <input matInput [value]="valueOf(f)" [attr.maxlength]="f.cells"
                                    (input)="setValue(f, $any($event.target).value)"
                                    [disabled]="fill.status === 'accepted'" />
                                <mat-hint align="end" *ngIf="f.cells">
                                    {{ valueOf(f).length }} / {{ f.cells }}
                                </mat-hint>
                            </mat-form-field>

                            <mat-form-field *ngIf="f.kind === 'multiline'" appearance="outline"
                                class="full" subscriptSizing="dynamic">
                                <textarea matInput rows="3" [value]="valueOf(f)"
                                    (input)="setValue(f, $any($event.target).value)"
                                    [disabled]="fill.status === 'accepted'"></textarea>
                            </mat-form-field>

                            <mat-form-field
                                *ngIf="f.kind !== 'checkbox' && f.kind !== 'radio' && f.kind !== 'choice'
                                       && f.kind !== 'comb' && f.kind !== 'multiline' && f.kind !== 'signature'"
                                appearance="outline" class="full" subscriptSizing="dynamic">
                                <input matInput [value]="valueOf(f)" [attr.maxlength]="f.maxLen"
                                    (input)="setValue(f, $any($event.target).value)"
                                    [disabled]="fill.status === 'accepted'" />
                            </mat-form-field>
                        </div>

                        <div class="field-meta">
                            <span class="disposition" [class.is-blank]="f.disposition === 'blank'"
                                [class.is-edited]="edits.has(f.fieldId)">
                                {{ dispositionLabel(f) }}
                            </span>

                            <span class="source" *ngIf="f.source && !edits.has(f.fieldId)">
                                <mat-icon>link</mat-icon>{{ sourceLabel(f) }}
                            </span>

                            <span class="conf" *ngIf="f.confidence !== null && !edits.has(f.fieldId)"
                                [ngClass]="confidenceClass(f)">
                                {{ (f.confidence * 100) | number:'1.0-0' }}%
                            </span>

                            <span class="spacer"></span>

                            <button mat-button class="revert quiet-button" *ngIf="edits.has(f.fieldId)"
                                (click)="$event.stopPropagation(); revert(f)">Undo</button>
                        </div>

                        <!-- The words in the record the answer rests on, so checking it is reading, not hunting. -->
                        <div class="field-quote" *ngIf="f.source?.quote && !edits.has(f.fieldId) && f.source?.kind !== 'user-supplied'">
                            <mat-icon>format_quote</mat-icon>
                            <span>{{ f.source?.quote }}</span>
                        </div>

                        <div class="field-note"
                            *ngIf="f.message && f.disposition === 'blank' && !edits.has(f.fieldId)">
                            <span class="reason-tag">{{ reasonLabel(f.reason || '') }}</span>
                            {{ f.message }}
                        </div>

                        <!-- Fit feedback comes from the code that will actually render it, so the
                             warning here and the finished PDF cannot disagree. -->
                        <div class="field-fit" *ngIf="fitFor(f) as fit">
                            <ng-container *ngIf="!fit.fits">
                                <mat-icon>warning_amber</mat-icon>
                                <span>Too long for the space — the form will show
                                    “{{ fit.wouldRender }}”{{ fit.fontSize ? ' at ' + fit.fontSize + 'pt' : '' }}.</span>
                            </ng-container>
                        </div>

                        <div class="field-finding" *ngFor="let x of f.findings"
                            [class.block]="x.severity === 'block'">
                            <mat-icon>{{ x.severity === 'block' ? 'report' : 'warning_amber' }}</mat-icon>
                            <span><strong>{{ reasonLabel(x.code) }}</strong> — {{ x.detail }}</span>
                        </div>
                    </div>
                </ng-container>
            </div>

            <!-- ══ RIGHT: the real document, updating as you type ═════════════════════ -->
            <div class="review-viewer">
                <ngx-extended-pdf-viewer *ngIf="pdfUrl && fill.lane !== 'docx'" [src]="pdfUrl"
                    [formData]="formData" (formDataChange)="onViewerFormData($event)"
                    [(page)]="viewerPage" [useBrowserLocale]="true" height="100%"
                    [showDownloadButton]="false" [showPrintButton]="true" [showOpenFileButton]="false"
                    [showPresentationModeButton]="false" [showFindButton]="true" [showZoomButtons]="true">
                </ngx-extended-pdf-viewer>

                <!-- Word has no page to render without a converter — say so plainly rather than
                     dressing a text dump up as a preview. -->
                <div class="viewer-placeholder" *ngIf="fill.lane === 'docx'">
                    <mat-icon>description</mat-icon>
                    <p><strong>Word documents have no live preview.</strong></p>
                    <p class="muted">Everything on the left is applied to the document itself.
                        Download it to see the finished page.</p>
                </div>

                <div class="viewer-placeholder" *ngIf="!pdfUrl && fill.lane !== 'docx'">
                    <mat-icon>hourglass_top</mat-icon>
                    <p><strong>The form is still being completed.</strong></p>
                    <p class="muted">It will appear here as soon as there is a page to show.</p>
                </div>
            </div>
        </div>
    </ng-container>
</div>

<div class="review-actions" *ngIf="fill && !loading">
    <span class="edit-note" *ngIf="editCount">
        {{ editCount }} change{{ editCount === 1 ? '' : 's' }} to apply
    </span>
    <span class="spacer"></span>

    <button mat-button class="quiet-button" (click)="close()">Close</button>

    <button mat-flat-button class="tone-button" *ngIf="fill.status === 'awaiting_review'"
        (click)="accept()" [disabled]="saving">
        <mat-spinner diameter="16" *ngIf="saving"></mat-spinner>
        <span>Accept</span>
    </button>

    <ng-container *ngIf="fill.status === 'accepted'">
        <button mat-stroked-button class="quiet-button" (click)="accept()" [disabled]="saving || !editCount">
            Apply changes
        </button>
        <button mat-flat-button class="tone-button" (click)="download('editable')">
            <mat-icon>file_download</mat-icon> Download
        </button>
        <button mat-stroked-button class="quiet-button" *ngIf="fill.canDownloadSigned"
            (click)="download('signed')">
            <mat-icon>verified</mat-icon> Signed copy
        </button>
        <button mat-stroked-button class="quiet-button" (click)="promoteToTimeline()"
            [disabled]="promoting || promoted"
            matTooltip="Adds it to the patient file, where it stays permanently">
            <mat-icon>{{ promoted ? 'check' : 'post_add' }}</mat-icon>
            {{ promoted ? 'In the timeline' : 'Add to timeline' }}
        </button>
    </ng-container>
</div>
